/**
 * Accessor functions for connection configuration fields.
 *
 * Per §TS.7.1: Generic utility functions with configuration objects.
 * Per §BASIC.2: Centralized config access to eliminate duplication across components.
 *
 * These functions provide a consistent interface for accessing connection
 * configuration values, handling null/undefined cases with sensible defaults.
 */

// 4. Type imports
import type { ConnectionResponse } from '~/generated/api/model';

/**
 * URL components parsed from a connection URL.
 * All fields are nullable to handle parsing failures gracefully.
 */
export interface UrlComponents {
  scheme: string | null;
  hostname: string | null;
  path: string | null;
}

/**
 * Returns the connection name or a default fallback.
 *
 * Reads from configSummary.name per Phase 1 schema change.
 * Uses optional chaining for forward compatibility with type regeneration.
 *
 * @param connection - Connection response object
 * @returns The connection name or 'Unnamed' if not set
 */
export function getConnectionName(connection: ConnectionResponse): string {
  const configSummary = connection.configSummary;
  return (configSummary?.name as string) || 'Unnamed';
}

/**
 * Returns the connection description or null if not set.
 *
 * Reads from configSummary.description per Phase 1 schema change.
 * Uses optional chaining for forward compatibility with type regeneration.
 *
 * @param connection - Connection response object
 * @returns The connection description or null
 */
export function getConnectionDescription(connection: ConnectionResponse): string | null {
  const configSummary = connection.configSummary;
  return (configSummary?.description as string) ?? null;
}

/**
 * Connection object carrying the transient, local-only `_authConfig` that the
 * detail page attaches when the user enters credentials. It never appears in a
 * server response.
 */
type ConnectionWithAuth = ConnectionResponse & { _authConfig?: Record<string, string> };

/**
 * Deterministically serializes a value with object keys sorted recursively, so
 * two structurally-equal objects produce identical strings regardless of key
 * insertion order. Arrays preserve order; undefined-valued keys are omitted to
 * mirror JSON.stringify semantics.
 */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value) ?? 'null';
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const record = value as Record<string, unknown>;
  const entries = Object.keys(record)
    // Treat null like a missing key: the backend serializes configSummary with
    // exclude_none, so a field the UI cleared to null comes back absent. A
    // strict compare would leave the form dirty after every such save.
    .filter((key) => record[key] !== undefined && record[key] !== null)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`);
  return `{${entries.join(',')}}`;
}

/**
 * Compares two connection objects by user-editable state only: `status`,
 * `configSummary` (order-insensitive deep compare), and the transient local
 * `_authConfig`.
 *
 * Server-managed fields (`updatedAt`, `createdAt`, `lastUsedAt`,
 * `lastProcessedSequence`, `messagesBehind`, `tokenExpiresAt`) are ignored. The
 * backend bumps `updatedAt` on every PATCH and the update mutation writes that
 * response back into the query cache, so comparing the whole object would mark
 * the form dirty after every save and re-fire the auto-save indefinitely. Used
 * as the `useAutoSave` `isEqual` so dirty detection reflects real edits.
 *
 * @param a - First connection object
 * @param b - Second connection object
 * @returns True when the user-editable state is identical
 */
export function connectionsEditableEqual(a: ConnectionResponse, b: ConnectionResponse): boolean {
  if (a.status !== b.status) {
    return false;
  }
  const aAuth = (a as ConnectionWithAuth)._authConfig;
  const bAuth = (b as ConnectionWithAuth)._authConfig;
  if (stableStringify(aAuth) !== stableStringify(bAuth)) {
    return false;
  }
  return stableStringify(a.configSummary) === stableStringify(b.configSummary);
}

/**
 * Checks if a connection config has a custom body template defined.
 *
 * Per spec: Returns true when config?.bodyTemplate != null
 *
 * @param config - Connection configSummary object
 * @returns True if a custom body template is defined
 */
export function hasCustomTemplate(config: Record<string, unknown> | undefined): boolean {
  return config?.bodyTemplate != null;
}

/**
 * Parses a URL string into its components using the browser URL API.
 *
 * Uses the native URL constructor for parsing. Returns null values for all
 * fields if the URL is undefined or malformed.
 *
 * @param url - URL string to parse
 * @returns Parsed URL components with scheme, hostname, and path
 */
export function parseUrlComponents(url: string | undefined): UrlComponents {
  if (!url) {
    return { scheme: null, hostname: null, path: null };
  }

  try {
    const parsed = new URL(url);
    return {
      scheme: parsed.protocol.replace(/:$/, ''),
      hostname: parsed.hostname,
      path: parsed.pathname,
    };
  } catch {
    return { scheme: null, hostname: null, path: null };
  }
}
